import { useState, useEffect, useMemo, Suspense } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  LayoutDashboard, Building2, Users, Wrench, HardHat, MessageSquare, Mail,
  ScrollText, Activity, LogOut, PanelLeft, ShieldCheck, ChevronDown, BarChart3,
  Sun, Moon, Monitor, Check, Inbox, ClipboardCheck, LayoutTemplate,
  Handshake, ClipboardList, Ticket,
} from 'lucide-react';
import cn from '../lib/cn';
import { initials } from '../lib/format';
import { useAuth } from '../context/AuthContext';
import { useAppearance } from '../context/AppearanceContext';
import Wordmark from './Wordmark';
import PageLoader from './PageLoader';
import { Badge, Divider } from './ui/primitives';
import { Tooltip } from './ui/overlays';

/**
 * The console shell, built as a macOS sidebar app.
 *
 * The recognisable pieces of the platform are all here: a translucent sidebar
 * with vibrancy, rounded selection pills that slide between items, a toolbar
 * that blurs content scrolling under it, and grouped section headers.
 * Navigation is grouped into three questions rather than a flat list of eleven
 * links — who is on the platform, what is happening on it, and how the platform
 * itself is doing.
 */

/**
 * Navigation, filtered by console role.
 *
 * An item with no `roles` is superadmin-only, which keeps the safe default the
 * same here as it is on the server: a link added without thinking about roles
 * does not leak into a field supervisor's sidebar. This is presentation only —
 * every one of these routes is enforced by the API, which re-reads the role
 * from the database on each request.
 */
const SECTIONS = [
  {
    heading: null,
    items: [
      { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
      { to: '/analytics', label: 'Tenant Analytics', icon: BarChart3 },
    ],
  },
  {
    heading: 'Field',
    items: [
      {
        to: '/field-jobs',
        label: 'Field Jobs',
        icon: ClipboardCheck,
        roles: ['superadmin', 'field_supervisor'],
      },
    ],
  },
  {
    // The distributor channel's own side of the console — see
    // jobcard-provisioning-flow.pdf. Empty for every role but the two that
    // touch it, same as 'Field' above.
    heading: 'Distributor',
    items: [
      {
        to: '/distributor',
        label: 'My Channel',
        icon: Handshake,
        roles: ['superadmin', 'distributor'],
      },
      {
        to: '/distributor/garages',
        label: 'My Garages',
        icon: Building2,
        roles: ['superadmin', 'distributor'],
      },
      {
        to: '/distributor/activation-codes',
        label: 'Activation Codes',
        icon: Ticket,
        roles: ['superadmin', 'distributor'],
      },
      {
        to: '/distributor/pro-requests',
        label: 'Pro Requests',
        icon: ClipboardList,
        roles: ['superadmin', 'distributor'],
      },
    ],
  },
  {
    heading: 'Tenants',
    items: [
      { to: '/organizations', label: 'Organizations', icon: Building2 },
      { to: '/users', label: 'Users', icon: Users },
    ],
  },
  {
    heading: 'Channel',
    items: [
      { to: '/distributors', label: 'Distributors', icon: Handshake },
      { to: '/pro-requests', label: 'Pro Requests', icon: ClipboardList },
    ],
  },
  {
    heading: 'Activity',
    items: [
      { to: '/operations', label: 'Operations', icon: Wrench },
      { to: '/technicians', label: 'Technicians', icon: HardHat },
      { to: '/feedback', label: 'Feedback', icon: MessageSquare },
    ],
  },
  {
    heading: 'Platform',
    items: [
      // The two directions of mail, adjacent: what the platform sends, and what
      // arrives at the domain.
      { to: '/mail', label: 'Garage Mail', icon: Mail },
      { to: '/incoming-mail', label: 'Incoming Mail', icon: Inbox },
      { to: '/landing-page', label: 'Landing Page', icon: LayoutTemplate },
      { to: '/audit', label: 'Audit Log', icon: ScrollText },
      { to: '/health', label: 'System Health', icon: Activity },
    ],
  },
];

/** The sections a console role may see, with empty groups dropped. */
function sectionsFor(role) {
  return SECTIONS
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => (item.roles || ['superadmin']).includes(role)),
    }))
    .filter((section) => section.items.length > 0);
}

/**
 * A sidebar row.
 *
 * The selected state is a filled rounded pill that animates between items with
 * a shared layout id — the Finder/Mail behaviour. The pill sits *behind* the
 * content so the label stays crisp while it moves.
 */
function NavItem({ item, onNavigate }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) => cn(
        'relative flex items-center gap-2.5 rounded-field px-2.5 py-[7px] text-subhead',
        'transition-colors duration-150 ease-control',
        isActive ? 'font-medium text-white' : 'text-label2 hover:bg-fill/10',
      )}
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <motion.span
              layoutId="sidebar-selection"
              className="absolute inset-0 -z-10 rounded-field bg-blue"
              transition={{ type: 'spring', stiffness: 500, damping: 38 }}
            />
          )}
          <Icon className={cn('h-[17px] w-[17px] shrink-0', isActive ? 'text-white' : 'text-label3')} />
          <span className="truncate">{item.label}</span>
        </>
      )}
    </NavLink>
  );
}

function SidebarContent({ onNavigate }) {
  const { user, security } = useAuth();
  // Recomputed only when the role changes, which is once per session.
  const sections = useMemo(() => sectionsFor(user?.role), [user?.role]);

  return (
    <div className="flex h-full flex-col">
      {/* Toolbar-height header so the sidebar title aligns with the content
          toolbar across the divider, as in every macOS sidebar app. */}
      <div className="flex h-14 shrink-0 flex-col justify-center gap-1 px-4">
        <Wordmark height={17} />
        <p className="text-caption2 leading-tight text-label3">Platform Console</p>
      </div>

      <nav className="flex-1 space-y-4 overflow-y-auto px-2.5 pb-3">
        {sections.map((section, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={section.heading || index}>
            {section.heading && <p className="eyebrow px-2.5 pb-1">{section.heading}</p>}
            <div className="space-y-0.5">
              {section.items.map((item) => (
                <NavItem key={item.to} item={item} onNavigate={onNavigate} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* A standing reminder of what this console can see. Every table below
          holds other people's customers; that should never feel routine. */}
      <div className="shrink-0 px-3 pb-3">
        <Tooltip content="Every action here is written to the audit log, including revealing masked contact details.">
          <div className="flex w-full items-center gap-2 rounded-field bg-fill/[0.08] px-2.5 py-2">
            <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-green" />
            <span className="text-caption leading-tight text-label3">All actions audited</span>
          </div>
        </Tooltip>
        {security && !security.mfaRequired && (
          <p className="mt-2 px-1 text-caption2 text-orange">2FA is disabled platform-wide.</p>
        )}
        {user && (
          <p className="mt-2 truncate px-1 text-caption2 text-label4" title={user.email}>
            {user.email}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Appearance switcher — Light / Dark / Auto, as macOS System Settings offers.
 * "Auto" follows the OS and is the default; an app that ignores the system
 * appearance feels foreign on the platform.
 */
function AppearanceMenu() {
  const { mode, setMode } = useAppearance();
  const options = [
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'dark', label: 'Dark', icon: Moon },
    { value: 'auto', label: 'Auto', icon: Monitor },
  ];

  return (
    <div className="px-1.5 py-1">
      <p className="px-2 pb-1 text-caption2 font-medium uppercase tracking-wide text-label3">
        Appearance
      </p>
      {options.map(({ value, label, icon: Icon }) => (
        <button
          key={value}
          type="button"
          role="menuitemradio"
          aria-checked={mode === value}
          onClick={() => setMode(value)}
          className="flex w-full items-center gap-2.5 rounded-control px-2 py-1.5 text-subhead text-label transition-colors hover:bg-fill/10"
        >
          <Icon className="h-3.5 w-3.5 text-label3" />
          <span className="flex-1 text-left">{label}</span>
          {mode === value && <Check className="h-3.5 w-3.5 text-blue" />}
        </button>
      ))}
    </div>
  );
}

export default function AppShell() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = () => setMenuOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [menuOpen]);

  useEffect(() => setSidebarOpen(false), [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex min-h-screen">
      {/* Desktop sidebar — vibrancy over the canvas, hairline divider. */}
      <aside className="material-sidebar hairline-r fixed inset-y-0 left-0 z-30 hidden w-[232px] lg:block">
        <SidebarContent />
      </aside>

      {/* Compact sidebar — a slide-over, as on iPad. */}
      <AnimatePresence>
        {sidebarOpen && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <motion.div
              className="absolute inset-0 bg-black/25"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setSidebarOpen(false)}
            />
            <motion.aside
              className="material-thick hairline-r relative h-full w-[264px]"
              initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }}
              transition={{ type: 'spring', stiffness: 380, damping: 40 }}
            >
              <SidebarContent onNavigate={() => setSidebarOpen(false)} />
            </motion.aside>
          </div>
        )}
      </AnimatePresence>

      <div className="flex min-w-0 flex-1 flex-col lg:pl-[232px]">
        {/* Toolbar: translucent, so content blurs as it scrolls underneath. */}
        <header className="material hairline-b sticky top-0 z-20 flex h-14 shrink-0 items-center gap-2 px-4">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            aria-label="Show sidebar"
            className="rounded-control p-1.5 text-label2 transition-colors hover:bg-fill/10 lg:hidden"
          >
            <PanelLeft className="h-4 w-4" />
          </button>

          <div className="flex-1" />

          <Badge tone={user?.mfaEnabled ? 'green' : 'orange'} dot>
            {user?.mfaEnabled ? '2FA on' : '2FA off'}
          </Badge>

          <div className="relative">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              className="flex items-center gap-1.5 rounded-control p-1 transition-colors hover:bg-fill/10"
            >
              <span className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-blue/15 text-caption font-semibold text-blue">
                {initials(user?.name || user?.email)}
              </span>
              <ChevronDown className={cn('h-3 w-3 text-label3 transition-transform', menuOpen && 'rotate-180')} />
            </button>

            {/* A macOS menu: material, tight radius, hairline dividers. */}
            <AnimatePresence>
              {menuOpen && (
                <motion.div
                  role="menu"
                  onClick={(e) => e.stopPropagation()}
                  initial={{ opacity: 0, y: -4, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -4, scale: 0.96 }}
                  transition={{ duration: 0.15, ease: [0.32, 0.72, 0, 1] }}
                  style={{ transformOrigin: 'top right' }}
                  className="material-thick absolute right-0 top-full z-30 mt-1.5 w-60 overflow-hidden rounded-card shadow-popover ring-1 ring-inset ring-separator"
                >
                  <div className="px-3 py-2.5">
                    <p className="truncate text-subhead font-medium text-label">{user?.name}</p>
                    <p className="truncate text-caption text-label3">{user?.email}</p>
                    {/* Read from the session, not hardcoded — a field
                        supervisor was previously labelled "superadmin" here,
                        which is exactly the wrong thing for this badge to be
                        wrong about. */}
                    <Badge
                      tone={user?.role === 'superadmin' ? 'red' : 'blue'}
                      className="mt-1.5"
                    >
                      {String(user?.role || '').replace(/_/g, ' ') || 'account'}
                    </Badge>
                  </div>

                  <Divider />
                  <AppearanceMenu />
                  <Divider />

                  <div className="p-1.5">
                    <button
                      type="button"
                      role="menuitem"
                      onClick={handleLogout}
                      className="flex w-full items-start gap-2.5 rounded-control px-2 py-1.5 text-left transition-colors hover:bg-fill/10"
                    >
                      <LogOut className="mt-0.5 h-3.5 w-3.5 shrink-0 text-label3" />
                      <span>
                        <span className="block text-subhead text-label">Sign Out</span>
                        {/* Named for what it does: signing out bumps
                            tokenVersion, ending every session, not just this tab. */}
                        <span className="mt-0.5 block text-caption2 leading-relaxed text-label3">
                          Ends all your sessions
                        </span>
                      </span>
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </header>

        <main className="min-w-0 flex-1 px-4 py-5 sm:px-6">
          {/* Keyed on pathname so a page's local state cannot leak across
              navigations, and each screen gets its own entrance. */}
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
          >
            {/* Every page below is lazily imported (see App.jsx). The boundary
                sits here, inside the shell, so a route change swaps only the
                content area — the sidebar, toolbar and selection state stay on
                screen. Placed around <Routes> instead, each navigation would
                blank the whole window and feel slower than the single large
                bundle this replaced. */}
            <Suspense fallback={<PageLoader />}>
              <Outlet />
            </Suspense>
          </motion.div>
        </main>
      </div>
    </div>
  );
}

/**
 * Page title block.
 *
 * `title1` at semibold is how Apple sets a large title — bold reads heavy in
 * SF at that size, which is why the HIG specifies semibold.
 */
export function PageHeader({ title, subtitle, actions, breadcrumb, className }) {
  return (
    <div className={cn('mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between', className)}>
      <div className="min-w-0">
        {breadcrumb}
        <h1 className="text-title1 font-semibold text-label">{title}</h1>
        {subtitle && (
          <p className="mt-1 max-w-2xl text-footnote leading-relaxed text-label3">{subtitle}</p>
        )}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}
